import Data from 'data';
import { getSkillValues } from 'logic/skills';
import { getStats } from 'logic/stats';
import createStore from './factory';
import Store from './index';

const identity = state => state;

function compareProfiles(prev, profile) {
  if (profile.seasonal !== prev.seasonal) {
    return "seasonal";
  }
  if (profile.items !== prev.items) {
    for (const slot in Data.itemSlots) {
      if ((profile.items[slot] || null) !== (prev.items[slot] || null)) {
        return slot;
      }
    }
  }
  if (profile.skills !== prev.skills) {
    return "skills";
  }
  if (profile.passives !== prev.passives) {
    return "passives";
  }
  if (profile.kanai !== prev.kanai) {
    return "kanai";
  }
  if (profile.paragon !== prev.paragon) {
    return "paragon";
  }
  if (profile.follower !== prev.follower || profile.followerItems !== prev.followerItems || profile.followerSkills !== prev.followerSkills) {
    return "follower";
  }
}

function syncProfile(state, profile, maxLength) {
  if (state) {
    for (let cur = state.next; cur; cur = cur.next) {
      if (cur.profile === profile) {
        let prev = state;
        while (prev.next !== cur) {
          const next = prev.next;
          delete prev.next;
          next.prev = prev;
          prev = next;
        }
        delete prev.next;
        return {...cur, prev};
      }
    }
    for (let cur = state.prev; cur; cur = cur.prev) {
      if (cur.profile === profile) {
        let next = state;
        while (next.prev !== cur) {
          const prev = next.prev;
          delete next.prev;
          prev.next = next;
          next = prev;
        }
        delete next.prev;
        return {...cur, next};
      }
    }
  }

  let change = null;
  if (!state) {
    change = "import";
  } else if (profile.class !== state.profile.class) {
    state = null;
    change = "class";
  } else if (profile !== state.profile) {
    change = compareProfiles(state.profile, profile);
  } else {
    return state;
  }
  if (change) {
    const dst = {profile, change, prev: state};
    if (state) {
      dst.values = state.values;
      dst.skillValues = state.skillValues;
      if (dst.change === state.change) dst.prev = state.prev;
    } else if (profile.values) {
      dst.values = profile.values;
    }
    if (dst.prev && dst.prev.next) {
      dst.prev = {...dst.prev};
      delete dst.prev.next;
    }
    if (maxLength) {
      let current = dst;
      let length = 1;
      while (current.prev && length < maxLength) {
        current = current.prev;
        length += 1;
      }
      if (current.prev) {
        // this mutates state, but nobody should rely on this being immutable!
        delete current.prev;
      }
    }
    return dst;
  } else {
    return {...state, profile};
  }
}

export default class HistoryStore extends createStore({
  syncProfiles(state, sourceState, values, skillValues, maxLength) {
    const newState = {};
    const active = sourceState.profiles[sourceState.activeProfile];
    for (const profile of sourceState.profiles) {
      let result = syncProfile(state[profile.id], profile, maxLength);
      if (profile === active) {
        result = {...result, values, skillValues};
      }
      newState[profile.id] = result;
    }
    return newState;
  },

  importValues(state, valuesMap) {
    state = {...state};
    for (const [id, values] of Object.entries(valuesMap)) {
      if (state[id]) {
        state[id] = {...state[id], values};
      }
    }
    return state;
  },
}, {}) {
  constructor(store, maxLength) {
    super();

    const update = state => {
      const stats = getStats(state);
      const values = {};
      for (const [id, {stat}] of Object.entries(Data.localeTable.statList)) {
        if (stat) values[id] = Math.floor(stats.getValue(stat));
      }
      const skillValues = getSkillValues(state);
      this.dispatch.syncProfiles(state, values, skillValues, maxLength);
    };

    update(store.getState());
    store.subscribe(identity, update);
  }

  importValues(valuesMap) {
    return new Promise((resolve, reject) => {
      let unsubscribe;
      const handler = () => {
        unsubscribe();
        resolve();
      };
      unsubscribe = this.store.subscribe(handler);
      this.dispatch.importValues(valuesMap);
    });
  }
}

export function useCanUndo() {
  const id = Store.useProfile(profile => profile.id);
  return HistoryStore.useSelector(state => !!state[id]?.prev);
}
